import { IonIcon, IonSpinner, IonText } from '@ionic/react';
import {
  chevronDownOutline,
  chevronUpOutline,
  reorderThreeOutline,
  trashOutline,
  musicalNotesOutline,
  checkmarkCircle,
  alertCircle,
  closeCircle,
} from 'ionicons/icons';
import React, { useEffect, useState, useMemo } from 'react';
import { iTrack } from '../../types/ITrack';
import './Tracks.scss';

import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSelector } from 'react-redux';
import { selectCrateState } from '../../store/slices/crate.slice';
import IntelligentMediaPlayer from '../media-player/IntelligentMediaPlayer';
import { EditableBPMField } from './EditableBPMField';
import { EditableKeyField } from './EditableKeyField';
import { EditableNumberField } from './EditableNumberField';
import { EditableTextField } from './EditableTextField';
import './Track.scss';
import isElectron from '../../utils/isElectron';
import { on, removeListener, run } from '../../helper/electron';
import sha256 from 'crypto-js/sha256';
import {
  selectAllSelectedMatches,
  selectAllTrackMatches,
} from '../../store/slices/track-matcher.slice';
import { generateTrackID } from '../../utils/generateTrackID';

export const TrackDetailsRow: React.FC<
  iTrack & {
    trackNum?: number;
    dragListeners?: SyntheticListenerMap;
    disabled?: boolean;
    onDeleteTrack?: (
      event: React.MouseEvent<HTMLIonIconElement, MouseEvent>,
    ) => void;
    onMoveTrackUp?: () => void;
    onMoveTrackDown?: () => void;
    isMoveUpDisabled?: boolean;
    isMoveDownDisabled?: boolean;
  }
> = (props) => {
  const {
    ID,
    Artist,
    Title,
    AlbumCover,
    BPM,
    energy,
    danceability,
    Key_Camelot,
    valence,
    PreviewURL,
    trackNum,
    dragListeners,
    onDeleteTrack,
    disabled,
    IsDriveMounted,
    onMoveTrackUp,
    onMoveTrackDown,
    isMoveUpDisabled = false,
    isMoveDownDisabled = false,
  } = props;
  const { isEditable } = useSelector(selectCrateState);
  const selectedMatches = useSelector(selectAllSelectedMatches);
  const trackMatches = useSelector(selectAllTrackMatches);

  const trackId = generateTrackID(props);
  const hasSelectedMatch = (selectedMatches[trackId]?.length ?? 0) > 0;
  const potentialMatches = trackMatches[trackId] || [];
  const maxScore = potentialMatches.reduce(
    (max, m) => Math.max(max, m.match_score ?? 0),
    0,
  );
  const matchStatus: 'matched' | 'review' | 'unmatched' = useMemo(() => {
    if (hasSelectedMatch || (potentialMatches.length > 0 && maxScore >= 80)) {
      return 'matched';
    }
    if (potentialMatches.length > 0) {
      return 'review';
    }
    return 'unmatched';
  }, [hasSelectedMatch, potentialMatches.length, maxScore]);

  const [isPreviewAvailable, setIsPreviewAvailable] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!PreviewURL) {
      setIsPreviewAvailable(false);
      return;
    }

    if (!isElectron()) {
      setIsPreviewAvailable(true);
      return;
    }

    const isRemotePreview = /^https?:\/\//i.test(PreviewURL);

    if (isRemotePreview) {
      setIsPreviewAvailable(true);
      return;
    }

    const requestId = `${Date.now()}-${Math.random()}`;
    let isSubscribed = true;

    const handleExistsResponse = (data: {
      requestId: string;
      exists: boolean;
    }) => {
      if (data.requestId === requestId && isSubscribed) {
        setIsPreviewAvailable(data.exists);
      }
    };

    on('check-file-exists-response', handleExistsResponse);

    run('check-file-exists', {
      requestId,
      filePath: PreviewURL,
    });

    return () => {
      isSubscribed = false;
      removeListener('check-file-exists-response', handleExistsResponse);
    };
  }, [PreviewURL]);

  const showMoveControls =
    isEditable &&
    (onMoveTrackUp !== undefined || onMoveTrackDown !== undefined);

  const handleMoveUpClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isMoveUpDisabled) {
      onMoveTrackUp?.();
    }
  };

  const handleMoveDownClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isMoveDownDisabled) {
      onMoveTrackDown?.();
    }
  };

  return (
    <div
      className={`track-row ${isEditable ? 'editable' : ''} ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {disabled && (
        <div className="track-loading-overlay">
          <IonSpinner name="crescent" />
          <span className="loading-text">Matching files...</span>
        </div>
      )}
      {/* Track Number / Drag Handle Column */}
      <div className="track-row-num">
        {isEditable && !disabled && isHovered ? (
          <div className="drag-handle" {...dragListeners}>
            <IonIcon icon={reorderThreeOutline} />
          </div>
        ) : (
          <div className="track-number-wrapper">
            <span className="track-number">{trackNum}</span>
            <div
              className={`match-status-indicator ${matchStatus}`}
              title={
                matchStatus === 'matched'
                  ? 'Matched to local file'
                  : matchStatus === 'review'
                    ? 'Needs Review (potential matches found)'
                    : 'Unmatched (no local files found)'
              }
            >
              <IonIcon
                icon={
                  matchStatus === 'matched'
                    ? checkmarkCircle
                    : matchStatus === 'review'
                      ? alertCircle
                      : closeCircle
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Play Button - Left side */}
      <div className="track-row-play">
        {isPreviewAvailable ? (
          <IntelligentMediaPlayer
            height={32}
            url={PreviewURL}
            isDriveMounted={IsDriveMounted}
          />
        ) : (
          <div className="play-placeholder" />
        )}
      </div>

      {/* Album Art */}
      <div className="track-row-art">
        {AlbumCover && !disabled ? (
          <img src={AlbumCover} alt="" className="track-album-art" />
        ) : (
          <div className="track-album-placeholder">
            <IonIcon icon={musicalNotesOutline} className="placeholder-icon" />
          </div>
        )}
      </div>

      <div className="track-row-content">
        {/* Title & Artist */}
        <div className="track-row-info">
          <div className="track-title-wrapper">
            <EditableTextField
              trackId={ID}
              text={Title}
              field="Title"
              textColor="light"
              disabled={disabled}
            />
          </div>
          <div className="track-artist-wrapper">
            <EditableTextField
              trackId={ID}
              text={Artist}
              field="Artist"
              textColor="medium"
              disabled={disabled}
            />
          </div>
        </div>

        {/* Audio Features - Stats first, then KEY/BPM to align with matched tracks */}
        <div className="track-row-features">
          <div
            className="feature-cell feature-stat"
            title="Energy - Track intensity"
          >
            <span className="feature-label">ENERGY</span>
            <EditableNumberField
              trackId={ID}
              field="energy"
              value={typeof energy == 'number' ? energy : 0}
            />
          </div>
          <div
            className="feature-cell feature-stat"
            title="Danceability - Suitability for dancing"
          >
            <span className="feature-label">DANCE</span>
            <EditableNumberField
              trackId={ID}
              field="danceability"
              value={typeof danceability == 'number' ? danceability : 0}
            />
          </div>
          <div
            className="feature-cell feature-stat"
            title="Mood - Musical positiveness (0=sad, 100=happy)"
          >
            <span className="feature-label">MOOD</span>
            <EditableNumberField
              trackId={ID}
              field="valence"
              value={typeof valence == 'number' ? valence : 0}
            />
          </div>
          <div className="feature-cell feature-key">
            <span className="feature-label">KEY</span>
            <EditableKeyField trackId={ID} keyVal={Key_Camelot || ''} />
          </div>
          <div className="feature-cell feature-bpm">
            <span className="feature-label">BPM</span>
            <EditableBPMField
              trackId={ID}
              bpmVal={typeof BPM == 'number' ? BPM : 0}
            />
          </div>
        </div>
      </div>

      {/* Actions Column */}
      <div className="track-row-actions">
        {/* Move Controls - Only show on hover in edit mode */}
        {showMoveControls && isHovered && (
          <div className="move-controls">
            <button
              className="move-btn"
              disabled={isMoveUpDisabled || disabled}
              onClick={handleMoveUpClick}
              aria-label="Move track up"
            >
              <IonIcon icon={chevronUpOutline} />
            </button>
            <button
              className="move-btn"
              disabled={isMoveDownDisabled || disabled}
              onClick={handleMoveDownClick}
              aria-label="Move track down"
            >
              <IonIcon icon={chevronDownOutline} />
            </button>
          </div>
        )}

        {/* Delete Button - Only show on hover in edit mode */}
        {isEditable && isHovered && (
          <button
            className="delete-btn"
            onClick={onDeleteTrack as any}
            disabled={disabled}
          >
            <IonIcon icon={trashOutline} />
          </button>
        )}
      </div>
    </div>
  );
};
